import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { 
  Shield,
  Lock,
  Eye,
  Server,
  Key,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  ArrowRight,
  Mail
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const securityFeatures = [
  {
    icon: Lock,
    title: 'Encryption Everywhere',
    description: 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.',
  },
  {
    icon: Key,
    title: 'Secure Authentication',
    description: 'Multi-factor authentication, SSO support, and secure session management protect your account.',
  },
  {
    icon: Server,
    title: 'Infrastructure Security',
    description: 'Hosted on SOC 2 Type II certified infrastructure with 24/7 monitoring and DDoS protection.',
  },
  {
    icon: Eye,
    title: 'Privacy by Design',
    description: 'Federated learning keeps your data on your device. AI improves without accessing your designs.',
  },
]

const certifications = [
  { name: 'SOC 2 Type II', status: 'Certified', description: 'Annual audit of security controls' },
  { name: 'GDPR', status: 'Compliant', description: 'EU data protection regulation' },
  { name: 'ISO 27001', status: 'In Progress', description: 'Information security management' },
  { name: 'CCPA', status: 'Compliant', description: 'California privacy regulation' },
]

const securityPractices = [
  {
    category: 'Application Security',
    practices: [
      'Regular penetration testing by third-party security firms',
      'Static and dynamic code analysis in CI/CD pipeline',
      'Dependency vulnerability scanning',
      'Web Application Firewall (WAF) protection',
      'Rate limiting and abuse prevention',
      'Content Security Policy (CSP) enforcement',
    ],
  },
  {
    category: 'Data Protection',
    practices: [
      'AES-256 encryption for data at rest',
      'TLS 1.3 for all data in transit',
      'Encryption key management with HSM',
      'Automated backup with encryption',
      'Data residency options for enterprise',
      'Secure deletion and data retention policies',
    ],
  },
  {
    category: 'Access Control',
    practices: [
      'Role-based access control (RBAC)',
      'Principle of least privilege',
      'Multi-factor authentication (MFA)',
      'SSO integration (SAML, OIDC)',
      'Session management and timeout',
      'Audit logging of all access',
    ],
  },
  {
    category: 'Infrastructure',
    practices: [
      'SOC 2 Type II certified cloud providers',
      'Network segmentation and isolation',
      'DDoS protection and mitigation',
      '24/7 security monitoring and alerting',
      'Regular security patching',
      'Disaster recovery and business continuity',
    ],
  },
]

const bugBountyScope = [
  'Authentication and authorization vulnerabilities',
  'Data exposure or leakage',
  'Server-side injection attacks',
  'Cross-site scripting (XSS)',
  'Cross-site request forgery (CSRF)',
  'Business logic flaws',
]

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="absolute inset-0 grid-pattern" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success-500/10 border border-success-500/20 mb-8"
            >
              <Shield className="w-4 h-4 text-success-400" />
              <span className="text-sm font-medium text-success-300">Security</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            >
              Security at
              <br />
              <span className="text-gradient">3DMakerly</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-surface-300 max-w-2xl leading-relaxed mb-8"
            >
              Your designs and data are protected by enterprise-grade security. 
              We take a security-first approach to everything we build.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Button variant="primary" size="lg">
                <FileText className="w-5 h-5" />
                Download Security Whitepaper
              </Button>
              <Button variant="secondary" size="lg">
                <Mail className="w-5 h-5" />
                Contact Security Team
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-16 border-y border-surface-800 bg-surface-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-xl bg-surface-800/50 border border-surface-700/50"
              >
                <div className="p-3 rounded-xl bg-success-500/10 w-fit mb-4">
                  <feature.icon className="w-6 h-6 text-success-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-surface-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Compliance & Certifications</h2>
            <p className="text-surface-400 max-w-2xl mx-auto">
              We maintain compliance with industry standards and regulations
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-xl bg-surface-900/50 border border-surface-700/50 text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-surface-800 mb-4">
                  <Shield className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="font-bold text-white mb-1">{cert.name}</h3>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${
                  cert.status === 'Certified' ? 'bg-success-500/20 text-success-400' :
                  cert.status === 'Compliant' ? 'bg-primary-500/20 text-primary-400' :
                  'bg-warning-500/20 text-warning-400'
                }`}>
                  {cert.status}
                </span>
                <p className="text-xs text-surface-500">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Practices */}
      <section className="py-20 bg-surface-900/30 border-y border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Security Practices</h2>
            <p className="text-surface-400 max-w-2xl mx-auto">
              Comprehensive security measures across every layer of our platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {securityPractices.map((category, index) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-xl bg-surface-800/50 border border-surface-700/50"
              >
                <h3 className="text-xl font-bold text-white mb-4">{category.category}</h3>
                <ul className="space-y-3">
                  {category.practices.map((practice) => (
                    <li key={practice} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                      <span className="text-surface-300 text-sm">{practice}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bug Bounty */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-warning-500/10 border border-warning-500/20 mb-6">
                <AlertTriangle className="w-4 h-4 text-warning-400" />
                <span className="text-sm font-medium text-warning-300">Bug Bounty Program</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Help us stay secure
              </h2>
              <p className="text-surface-400 mb-6">
                We partner with security researchers to identify and fix vulnerabilities. 
                Report security issues responsibly and earn rewards.
              </p>
              
              <h3 className="font-semibold text-white mb-3">In Scope:</h3>
              <ul className="space-y-2 mb-6">
                {bugBountyScope.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-surface-400">
                    <CheckCircle className="w-4 h-4 text-success-400" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-4">
                <Button variant="primary">
                  View Bug Bounty Policy
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button variant="secondary">
                  Report a Vulnerability
                </Button>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gradient-to-br from-warning-900/20 to-surface-900 border border-warning-500/20"
            >
              <h3 className="text-2xl font-bold text-white mb-4">Reward Tiers</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50">
                  <div>
                    <p className="font-medium text-white">Critical</p>
                    <p className="text-xs text-surface-500">RCE, Auth bypass, Data breach</p>
                  </div>
                  <span className="text-success-400 font-bold">£5,000 - £15,000</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50">
                  <div>
                    <p className="font-medium text-white">High</p>
                    <p className="text-xs text-surface-500">Significant data exposure</p>
                  </div>
                  <span className="text-success-400 font-bold">£1,000 - £5,000</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50">
                  <div>
                    <p className="font-medium text-white">Medium</p>
                    <p className="text-xs text-surface-500">Limited impact vulnerabilities</p>
                  </div>
                  <span className="text-success-400 font-bold">£250 - £1,000</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50">
                  <div>
                    <p className="font-medium text-white">Low</p>
                    <p className="text-xs text-surface-500">Minor issues</p>
                  </div>
                  <span className="text-success-400 font-bold">£50 - £250</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise Security */}
      <section className="py-20 bg-surface-900/30 border-t border-surface-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-500/10 mb-4">
              <Users className="w-6 h-6 text-primary-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Enterprise Security</h2>
            <p className="text-surface-400 mb-8 max-w-xl mx-auto">
              Need additional security features? Our Enterprise plan includes SSO, 
              advanced audit logs, dedicated infrastructure, and custom compliance requirements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/pricing">
                <Button variant="ai" size="lg">
                  View Enterprise Plans
                </Button>
              </Link>
              <Button variant="secondary" size="lg">
                <Mail className="w-5 h-5" />
                Contact Security Team
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
